import { useState, useEffect, useRef } from 'react';
import { uploadApi } from '../../services/api';
import {
  X, UploadCloud, Search, Trash2, Check,
  Image as ImageIcon, Loader2, FileText, Download
} from 'lucide-react';
import { toast } from 'sonner';

export default function FileManagerModal({ onClose, onSelect, activeUrl }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const data = await uploadApi.getAll();
      // Ensure data is array
      setFiles(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to load media files');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate size (max 5MB)
    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds the 5MB limit');
      return;
    }

    setUploading(true);
    try {
      const res = await uploadApi.upload(selectedFile);
      toast.success('Image uploaded successfully!');
      fetchFiles();
      if (res.data?.url) {
        // Option to automatically select the newly uploaded file
        onSelect(res.data.url);
      }
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (e, filename) => {
    e.stopPropagation(); // prevent selection click
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) return;

    try {
      await uploadApi.delete(filename);
      toast.success('File deleted');
      setFiles(prev => prev.filter(f => f.filename !== filename));
    } catch (err) {
      toast.error(err.message || 'Failed to delete file');
    }
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredFiles = files.filter(f =>
    f.filename.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white border border-gray-100 rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col h-[80vh] animate-scale-up">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="text-slate-800 font-semibold font-outfit text-lg flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-[#47ace2]" />
              Media Manager
            </h3>
            <p className="text-slate-400 text-xs font-manrope mt-0.5">Select, upload or manage project images</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4 flex-shrink-0 bg-slate-50/50">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by filename..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-slate-700 text-sm font-manrope placeholder-slate-400 focus:outline-none focus:border-[#47ace2] focus:ring-2 focus:ring-[#47ace2]/10 transition-all shadow-sm"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleUpload}
              accept="image/*"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#47ace2] hover:bg-[#1f7db3] text-white rounded-xl font-manrope text-sm font-medium transition-all shadow-sm shadow-[#47ace2]/25 disabled:opacity-75"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <UploadCloud className="w-4 h-4" />
                  Upload Image
                </>
              )}
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/20">
          {loading ? (
            <div className="h-full flex items-center justify-center flex-col text-slate-400 gap-2">
              <Loader2 className="w-8 h-8 animate-spin text-[#47ace2]" />
              <p className="font-manrope text-sm">Loading media items...</p>
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="h-full flex items-center justify-center flex-col text-slate-400 py-16 border-2 border-dashed border-slate-200 rounded-2xl bg-white max-w-xl mx-auto my-4">
              <UploadCloud className="w-12 h-12 text-slate-300 mb-3" />
              <h4 className="text-slate-600 font-semibold font-outfit">No images found</h4>
              <p className="text-slate-400 text-xs font-manrope text-center max-w-xs mt-1">
                {search ? "No files match your search criteria." : "Upload project images using the button above to begin filling your media library."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredFiles.map((file) => {
                const isSelected = activeUrl === file.url;
                return (
                  <div
                    key={file.filename}
                    onClick={() => onSelect(file.url)}
                    className={`group relative bg-white border rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 ${
                      isSelected ? 'border-[#47ace2] ring-2 ring-[#47ace2]/20' : 'border-slate-200'
                    }`}
                  >
                    {/* Thumbnail preview */}
                    <div className="aspect-square bg-slate-50 flex items-center justify-center overflow-hidden border-b border-slate-100 relative">
                      <img
                        src={file.url}
                        alt={file.filename}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden absolute inset-0 bg-slate-50 items-center justify-center text-slate-400">
                        <FileText className="w-8 h-8 opacity-40" />
                      </div>

                      {/* Selected Indicator overlay */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#47ace2] text-white flex items-center justify-center shadow-md animate-scale-up">
                          <Check className="w-3.5 h-3.5" />
                        </div>
                      )}
                    </div>

                    {/* Meta info */}
                    <div className="p-3">
                      <p className="text-slate-700 font-manrope text-xs font-semibold truncate" title={file.filename}>
                        {file.filename}
                      </p>
                      <p className="text-slate-400 text-[10px] font-manrope mt-1">
                        {formatSize(file.size)}
                      </p>
                    </div>

                    {/* Action buttons (Visible on hover) */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); onSelect(file.url); }}
                        className="p-2 bg-white text-slate-700 rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
                        title="Select Image"
                      >
                        <Check className="w-4 h-4 text-[#47ace2]" />
                      </button>
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 bg-white text-slate-700 rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
                        title="Preview"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      <button
                        onClick={(e) => handleDelete(e, file.filename)}
                        className="p-2 bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition-colors shadow-sm"
                        title="Delete Media"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-slate-50 flex justify-end flex-shrink-0 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl border border-gray-200 bg-white text-slate-600 hover:bg-gray-50 font-manrope text-sm transition-colors shadow-sm"
          >
            Close
          </button>
        </div>

      </div>
    </div>
  );
}
